import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {Button, Heading} from '@primer/react'
import {useState} from 'react'

import {LabelCreate} from './LabelCreate'
import {LabelList} from './LabelList'
import {SearchBar} from './LabelSearchBar'
import type {RepositoryNode} from './repository-label-index-page-query-types'
import styles from './RepositoryLabel.module.css'

export type RepositoryLabelsInternalFragment = Pick<
  RepositoryNode,
  'id' | 'nameWithOwner' | 'labels' | 'isWritable' | 'viewerCanPush'
>

type RepositoryLabelsInternalProps = {
  repository: RepositoryLabelsInternalFragment
  queryKey?: RouteQueryKey
  isCreateDialogOpen?: boolean
  onCreateDialogOpenChange?: (open: boolean) => void
}

export function RepositoryLabelsInternal({
  repository,
  queryKey,
  isCreateDialogOpen: controlledOpen,
  onCreateDialogOpenChange,
}: RepositoryLabelsInternalProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = onCreateDialogOpenChange !== undefined
  const isCreateDialogOpen = isControlled ? !!controlledOpen : uncontrolledOpen
  const setIsCreateDialogOpen = isControlled ? onCreateDialogOpenChange : setUncontrolledOpen

  const data = repository

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  return (
    <>
      <div className={sidebarLayoutEnabled ? undefined : styles.middlePaneWrapper}>
        <div className={styles.header}>
          {!sidebarLayoutEnabled && (
            <div className={styles.titleGroup}>
              <SidebarPageLayout.SidebarTrigger />
              <Heading as="h1" className={styles.heading}>
                Labels
              </Heading>
            </div>
          )}
          {!isControlled && data.viewerCanPush && (
            <Button variant="primary" onClick={() => setIsCreateDialogOpen(true)}>
              New label
            </Button>
          )}
        </div>
        <SearchBar />
        <ErrorBoundary boundaryName="LabelsPage" fallback={<div>Oops we could not load the labels</div>}>
          <LabelList repository={data} onCreateLabel={() => setIsCreateDialogOpen(true)} queryKey={queryKey} />
        </ErrorBoundary>
      </div>
      <LabelCreate
        repository={data}
        isOpen={isCreateDialogOpen}
        setDialogOpen={setIsCreateDialogOpen}
        queryKey={queryKey}
      />
    </>
  )
}
